import { planTrimPurchase } from './trim-purchase.js';
import { effectiveTrimLengthMM } from './trim-capture.js';
import { wallEdgeRuns } from './wall-edge-runs.js';
import { measuredEdgeMM, wallAlong, wallLocalScale, openGroupLead } from './measured-edge.js';
/** The iPad's edge answer → the trim profile it means. Anything else (skirting, scotia, none) carries no edge trim. */
export const EDGE_FINISH_PROFILE = { l_trim: 'L Trim', u_trim: 'U Channel', silicone: 'Silicone' };
const r3 = (n) => Math.round(n * 1000) / 1000;
function metresPerPixel(floor) {
    const mm = Number(floor.scaleMmPerPx);
    return Number.isFinite(mm) && mm > 0 ? mm / 1000 : null;
}
function polygonPerimeter(points) {
    if (points.length < 2)
        return 0;
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        sum += Math.hypot(b.x - a.x, b.y - a.y);
    }
    return sum;
}
function distanceToOutline(p, poly) {
    let best = Number.POSITIVE_INFINITY;
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i], b = poly[(i + 1) % poly.length];
        const dx = b.x - a.x, dy = b.y - a.y;
        const len2 = dx * dx + dy * dy;
        const t = len2 < 1e-9 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
        best = Math.min(best, Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)));
    }
    return best;
}
/**
 * How much of a room's outline (metres, scaled points) lies along a non-wall opening.
 * The historical helper name is retained; callers now include BIR/storage fronts as well as open-plan divisions.
 * Correction, 20 October: the previous comment that retained robe fronts was wrong. The opening carries no board;
 * the recess's solid interior walls are a separate section. Within 0.15 m: the division sits BETWEEN the two
 * rooms traced either side of it (Living and Kitchen are 100 mm apart on Joanna's plan; 0.08 m missed it).
 */
export function openPlanAlongOutline(poly, edges, tol = 0.15) {
    let total = 0;
    for (let i = 0; i < poly.length; i++) {
        const p = poly[i], q = poly[(i + 1) % poly.length];
        const L = Math.hypot(q.x - p.x, q.y - p.y);
        if (L < 1e-6)
            continue;
        const ux = (q.x - p.x) / L, uy = (q.y - p.y) / L;
        const spans = [];
        for (const e of edges) {
            // Parallel and on the line, within tol; then the overlap along this outline edge.
            const off = (pt) => Math.abs((pt.x - p.x) * uy - (pt.y - p.y) * ux);
            if (off(e.a) > tol || off(e.b) > tol)
                continue;
            const t1 = (e.a.x - p.x) * ux + (e.a.y - p.y) * uy, t2 = (e.b.x - p.x) * ux + (e.b.y - p.y) * uy;
            const lo = Math.max(0, Math.min(t1, t2)), hi = Math.min(L, Math.max(t1, t2));
            if (hi - lo > tol)
                spans.push([lo, hi]);
        }
        spans.sort((x, y) => x[0] - y[0]);
        let end = -Infinity;
        for (const [lo, hi] of spans) {
            const from = Math.max(lo, end);
            if (hi > from)
                total += hi - from;
            end = Math.max(end, hi);
        }
    }
    return total;
}
export function skirtingPlan(floor) {
    const register = (floor.registers ?? []).find((r) => String(r.kind ?? '') === 'skirting');
    const value = String(register?.value ?? '');
    // The register's exceptions read as [areaId, answer, areaId, answer, …]: those rooms take their own answer.
    const exceptions = new Map();
    const list = (register?.exceptions ?? []).map((e) => String(e));
    for (let i = 0; i + 1 < list.length; i += 2)
        exceptions.set(list[i], list[i + 1]);
    const rooms = (floor.areas ?? [])
        .filter((a) => ((a.boundary ?? []).length >= 3 || (a.trims ?? []).length > 0 || (floor.trimRuns ?? []).some(t => t.areaID === a.id)) && !!String(a.finish ?? '').trim() && !/^none$/i.test(String(a.finish ?? '').trim()))
        .map((a) => {
        const per = metresPerPixel(floor);
        const scaled = per ? (a.boundary ?? []).map((p) => ({ x: Number(p.x) * per, y: Number(p.y) * per })) : [];
        const physical = !!floor.walls?.length && !!per;
        const lead = physical ? openGroupLead(floor, a) : null;
        const edgeMM = (edge) => {
            const p = a.boundary[edge], q = a.boundary[(edge + 1) % a.boundary.length], px = Math.hypot(q.x - p.x, q.y - p.y);
            const mm = measuredEdgeMM(a, p, q) ?? (lead ? measuredEdgeMM(lead, p, q) : null);
            const local = wallLocalScale(wallAlong(floor, p, q));
            return mm ?? px * (local ?? Number(a.scaleMmPerPx ?? floor.scaleMmPerPx ?? 0));
        };
        const doorScale = (edge) => { const p = a.boundary[edge], q = a.boundary[(edge + 1) % a.boundary.length]; return edgeMM(edge) / Math.hypot(q.x - p.x, q.y - p.y); };
        const edgeMetres = (runs) => runs.reduce((n, r) => n + edgeMM(r.edge) / 1000 * (r.to - r.from), 0);
        const outline = physical ? edgeMetres(wallEdgeRuns({ ...floor, wallDoors: [], openEdges: [] }, { ...a, doorways: [] })) : per ? polygonPerimeter(scaled) : 0;
        /**
         * EVERY OPENING ON THIS ROOM'S OUTLINE — its own doorways AND the doors hung on a wall.
         *
         * A door can be recorded two ways (the room claims it, or it hangs off a wall line), and a board
         * stops at the jamb either way. Counting only the room's own left Living 1.54 m too long, Bedroom 1
         * 0.72 m, Bedroom 3 0.72 m and the Hallway 2.16 m — the wall doors along their boundaries, which is
         * exactly the gap between this reader's first cut (33.4 / 36.2) and the app (31.8 / 32.6).
         */
        const poly = per ? (a.boundary ?? []).map((p) => ({ x: Number(p.x) * per, y: Number(p.y) * per })) : [];
        const onBoundary = (p) => {
            let best = Number.POSITIVE_INFINITY;
            for (let i = 0; i < poly.length; i++)
                best = Math.min(best, distanceToOutline(p, [poly[i], poly[(i + 1) % poly.length]]));
            return best;
        };
        const own = (a.doorways ?? []).reduce((n, d) => n + Number(d.widthMM ?? 0) / 1000, 0);
        const hung = (floor.wallDoors ?? []).reduce((n, d) => {
            const wall = d.wallID ? (floor.walls ?? []).find((w) => w.id && w.id === d.wallID) : (floor.walls ?? [])[Number(d.wallIndex ?? -1)];
            if (!wall?.a || !wall?.b || !per)
                return n;
            // The opening's centre, along its wall — the same placement the plan draws it at.
            const len = Math.hypot(Number(wall.b.x) - Number(wall.a.x), Number(wall.b.y) - Number(wall.a.y));
            if (len <= 0.01)
                return n;
            const ux = (Number(wall.b.x) - Number(wall.a.x)) / len, uy = (Number(wall.b.y) - Number(wall.a.y)) / len;
            const at = Number(d.posMM ?? 0);
            const mid = { x: (Number(wall.a.x) + ux * at) * per, y: (Number(wall.a.y) + uy * at) * per };
            return onBoundary(mid) <= 0.15 ? n + Number(d.widthMM ?? 0) / 1000 : n;
        }, 0);
        /**
         * Its own doorways, plus the doors hung on a wall along its outline.
         *
         * A room's door can be recorded either way — claimed by the room, or hung off a wall line — and a
         * board stops at the jamb either way. NOT included: the doorways of neighbouring rooms. A door
         * between two rooms IS an opening on both outlines, but matching them by geometry also caught doors
         * that merely run near an edge, and over-deducting a room that has no opening there is the worse
         * error of the two. The app's own figure for this job is 31.8 GF + 32.6 1F; this reproduces the
         * Ground exactly and the First to within 0.75 LM, and `FloorV15.skirtingPlan()` remains the source
         * of record — ported exactly the day it matters more than it does today.
         */
        const afterDoors = physical ? edgeMetres(wallEdgeRuns({ ...floor, openEdges: [] }, a, doorScale)) : 0;
        const doors = physical ? Math.max(0, outline - afterDoors) : own + hung;
        // A stretch already carrying an edging trim is not skirting; scotia is its own line.
        const runs = [...(a.trims ?? []), ...(floor.trimRuns ?? []).filter(t => t.areaID === a.id)];
        const trim = runs.filter(t => !['scotia', 'skirting'].includes(t.category ?? '') && t.coverage !== 'additional' && !t.anchor?.doorway && !t.anchor?.free)
            .reduce((n, t) => n + effectiveTrimLengthMM(t, floor) / 1000, 0);
        const boards = (kind) => runs.filter(t => t.category === kind);
        const boardLength = (kind) => boards(kind).reduce((n, t) => n + effectiveTrimLengthMM(t, floor) / 1000, 0);
        const replaces = (kind) => boards(kind).some(t => t.coverage === 'replaceRoom' || (kind === 'skirting' && !t.coverage));
        // The register excludes the room, or the room takes the floor's answer.
        const answer = exceptions.get(String(a.id)) ?? value;
        /**
         * NOT RECORDED IS NOT "NONE". Jacky, 5 October 2026, on Ameer (J-0826-10211), whose floor had no
         * skirting or scotia answer and so read 0 LM everywhere: *"when room selected, those length should
         * auto be loaded, regardless what is selected, so proposal & PO is not stuck waiting for it"*.
         * A selected room with no answer carries its edge run; an answer — including a room's "none" —
         * still decides. Whether the line is supplied is the Proposal's switch, not this length.
         */
        const skirtingRecorded = answer.trim() !== '';
        const included = !skirtingRecorded || (answer.replace(/[^a-z]/gi, '').toLowerCase().includes('skirting') && !/^none$/i.test(answer));
        // All open fronts, including BIR/storage. Their solid interior walls are a separate section.
        const open = physical ? Math.max(0, afterDoors - edgeMetres(wallEdgeRuns(floor, a, doorScale))) : per ? openPlanAlongOutline(scaled, (floor.openEdges ?? []).filter((e) => e.a && e.b).map((e) => ({ a: { x: Number(e.a.x) * per, y: Number(e.a.y) * per }, b: { x: Number(e.b.x) * per, y: Number(e.b.y) * per } }))) : 0;
        const run = Math.max(0, outline - doors - trim - open);
        const scotiaRegister = (floor.registers ?? []).find(r => r.kind === 'scotia');
        const scotiaExceptions = scotiaRegister?.exceptions ?? [];
        const exceptionIndex = scotiaExceptions.indexOf(String(a.id));
        const scotiaAnswer = exceptionIndex >= 0 ? scotiaExceptions[exceptionIndex + 1] : scotiaRegister?.value;
        const scotiaRecorded = String(scotiaAnswer ?? '').trim() !== '';
        const calculatedSkirting = boardLength('skirting') + (!replaces('skirting') && included ? run : 0);
        const calculatedScotia = boardLength('scotia') + (!replaces('scotia') && (!scotiaRecorded || scotiaAnswer !== 'none') ? run : 0);
        const mobileSkirting = Number(a.skirtingLM), mobileScotia = Number(a.scotiaLM);
        return {
            area_id: String(a.id ?? ''), name: (a.name ?? '').trim() || 'Room',
            outline_m: r3(outline), doorway_m: r3(doors), trim_m: r3(trim), open_m: r3(open),
            // A zero the app wrote for an unanswered register is "not recorded" too, never a measurement.
            skirting_m: !physical && a.skirtingLM != null && Number.isFinite(mobileSkirting) && mobileSkirting >= 0 && (skirtingRecorded || mobileSkirting > 0) ? r3(mobileSkirting) : r3(calculatedSkirting),
            scotia_m: !physical && a.scotiaLM != null && Number.isFinite(mobileScotia) && mobileScotia >= 0 && (scotiaRecorded || mobileScotia > 0) ? r3(mobileScotia) : r3(calculatedScotia),
        };
    });
    const sum = (k) => r3(rooms.reduce((n, room) => n + room[k], 0));
    const mobileSkirting = Number(floor.skirtingLM), mobileScotia = Number(floor.scotiaLM);
    const loose = (kind) => (floor.trimRuns ?? []).filter(t => !t.areaID && t.category === kind).reduce((n, t) => n + effectiveTrimLengthMM(t, floor) / 1000, 0);
    return {
        register: value,
        run_lm: r3(rooms.reduce((n, room) => n + Math.max(0, room.outline_m - room.doorway_m - room.trim_m - room.open_m), 0)),
        skirting_lm: !floor.walls?.length && floor.skirtingLM != null && Number.isFinite(mobileSkirting) && mobileSkirting >= 0 && (value.trim() !== '' || mobileSkirting > 0) ? r3(mobileSkirting) : r3(sum('skirting_m') + loose('skirting')),
        scotia_lm: !floor.walls?.length && floor.scotiaLM != null && Number.isFinite(mobileScotia) && mobileScotia >= 0 && (String((floor.registers ?? []).find(r => r.kind === 'scotia')?.value ?? '').trim() !== '' || mobileScotia > 0) ? r3(mobileScotia) : r3(sum('scotia_m') + loose('scotia')),
        rooms,
    };
}
/**
 * THE ONE READER OF MEASURED RUNS. Every scotia and trim length anything derives from the drawing comes out of
 * here — the Proposal builder, the Orders list and the plan page — so they can never disagree on the set.
 * Until 26 October 2026 the Proposal builder had its own reader (doorway trims and fixture edges only) and the
 * Orders list another (every drawn trim, no fixture edges), which is how Rosie's typed Kitchen L trims were ordered
 * but never quoted. A run names its HOST room (`area_id`, `area`) and where it came from (`source`).
 *
 *   · scotia: the room's wall run by the scotia register (an unanswered register still loads the run — Ameer, 5 Oct
 *     2026), plus every scotia board drawn on it;
 *   · trims: every non-scotia trim on a scoped room — a doorway transition, a typed length, a drawn polyline;
 *   · fixture edges: a cutout the floor stops at, its sides less any along a wall (SectionEdgesV15 exposedRunMM);
 *   · robe edges: a recess the floor runs into, its walls less its open face, by its own edge answer else its room's.
 *
 * A room is scoped when it has a finish other than None and is not kept (`newFloor.action === 'keep'`).
 */
export function assessmentAccessoryRuns(document) {
    const result = [];
    const edgeDefault = typeof document?.edgeDefault === 'string' && document.edgeDefault.trim() ? document.edgeDefault : undefined;
    for (const [fi, floor] of (document?.floors ?? []).entries()) {
        const name = floor.name ?? String(fi + 1);
        const kept = (a) => (a.newFloor?.action ?? (String(a.finish ?? '') === 'None' ? 'keep' : 'remove')) === 'keep';
        const scoped = (floor.areas ?? []).filter(a => !!a.finish?.trim() && !/^none$/i.test(a.finish.trim()) && !kept(a));
        const ids = new Set(scoped.map(a => a.id));
        const plan = skirtingPlan(floor);
        // ONE RUN PER SCOTIA ROOM — its wall run plus any scotia board drawn on it (skirtingPlan's scotia_m). The id is the
        // room's (`scotia:<floor index>:<area id>`), the same id a Proposal's frozen plan yields for that room (planRunIds), so
        // a Proposal row and the Orders list claim the room, never a fragment of it.
        for (const room of plan.rooms)
            if (room.scotia_m > 0 && ids.has(room.area_id)) {
                const area = floor.areas?.find(a => a.id === room.area_id);
                const drawn = [...(area?.trims ?? []), ...(floor.trimRuns ?? []).filter(t => t.areaID === room.area_id)].some(t => t.category === 'scotia');
                result.push({ id: `scotia:${fi}:${room.area_id}`, kind: 'scotia', profile: 'Scotia', floor: name, room: room.name, area_id: room.area_id, area: room.name, length_m: r3(room.scotia_m), source: drawn ? 'drawn' : 'wall_run' });
            }
        for (const [i, t] of (floor.trimRuns ?? []).filter(t => !t.areaID && t.category === 'scotia').entries()) {
            const length = r3(effectiveTrimLengthMM(t, floor) / 1000);
            if (length > 0)
                result.push({ id: t.id ?? `scotia:${fi}:loose:${i}`, kind: 'scotia', profile: 'Scotia', floor: name, room: t.location ?? '', area_id: '', area: '', length_m: length, source: 'drawn' });
        }
        const trims = [...scoped.flatMap(a => (a.trims ?? []).map(t => ({ t, room: a.name ?? '', area_id: String(a.id ?? '') }))), ...(floor.trimRuns ?? []).filter(t => !t.areaID || ids.has(t.areaID)).map(t => ({ t, room: scoped.find(a => a.id === t.areaID)?.name ?? '', area_id: String(t.areaID ?? '') }))];
        const seen = new Set();
        for (const [ti, { t, room, area_id }] of trims.entries()) {
            if (['scotia', 'skirting'].includes(t.category ?? ''))
                continue;
            const id = t.id ?? `${fi}:${ti}`;
            if (seen.has(id))
                continue;
            seen.add(id);
            const source = t.transitionKey || t.anchor?.doorway || t.measurementSource === 'doorway' ? 'doorway' : t.measurementSource === 'typed' ? 'typed' : 'drawn';
            const segments = t.measurementSource && t.segments?.length ? t.segments : [{ id, name: t.location ?? room, lengthMM: effectiveTrimLengthMM(t, floor) }];
            for (const segment of segments) {
                const length = segment.lengthMM / 1000;
                result.push({ id: segment.id, kind: 'trims', profile: t.type || t.category || 'Trim', floor: name, room: segment.name || room, area_id, area: room, length_m: Number.isFinite(length) && length > 0 ? r3(length) : 0, source });
            }
        }
        // Fixture edges and robe edges: geometry, so they need the floor's scale.
        const scale = Number(floor.scaleMmPerPx);
        if (!(scale > 0))
            continue;
        const walls = (floor.walls ?? []).filter((w) => !!w.a && !!w.b);
        const near = (p, a, b) => distanceToOutline(p, [a, b]);
        for (const a of scoped) {
            const areaId = String(a.id ?? ''), tol = 150 / scale;
            for (const [ci, c] of (a.cutouts ?? []).entries()) {
                const finish = typeof c.edgeFinish === 'string' && c.edgeFinish.trim() ? c.edgeFinish : undefined;
                if (!finish || c.coveringStops === false)
                    continue;
                const profile = EDGE_FINISH_PROFILE[finish];
                if (!profile)
                    continue;
                const pts = cutoutOutline(c, scale);
                let run = 0;
                pts.forEach((p, i) => { const q = pts[(i + 1) % pts.length]; if (!walls.some(w => near(p, w.a, w.b) <= tol && near(q, w.a, w.b) <= tol))
                    run += Math.hypot(q.x - p.x, q.y - p.y); });
                const length = r3(run * scale / 1000);
                if (length > 0)
                    result.push({ id: c.id ?? `edge:${fi}:${areaId}:${ci}`, kind: 'trims', profile, floor: name, room: c.kind ? `${a.name ?? ''} · ${c.kind}` : a.name ?? '', area_id: areaId, area: a.name ?? '', length_m: length, source: 'fixture_edge' });
            }
        }
        for (const [ei, e] of (floor.openEdges ?? []).entries()) {
            if (e.kind === 'openPlan' || e.floorCovered === false)
                continue;
            const poly = (e.recessPolygon ?? []).filter((p) => !!p && Number.isFinite(p.x) && Number.isFinite(p.y));
            if (poly.length < 3 || !e.a || !e.b)
                continue;
            const mid = { x: (e.a.x + e.b.x) / 2, y: (e.a.y + e.b.y) / 2 };
            const host = scoped.map(r => ({ r, d: (r.boundary ?? []).length >= 2 ? Math.min(...(r.boundary ?? []).map((p, i, all) => near(mid, p, all[(i + 1) % all.length]))) : Number.POSITIVE_INFINITY })).filter(x => Number.isFinite(x.d) && x.d <= 24).sort((x, y) => x.d - y.d)[0]?.r;
            if (!host)
                continue;
            const finish = (typeof e.edgeFinish === 'string' && e.edgeFinish.trim() ? e.edgeFinish : undefined) ?? (typeof host.edgeFinish === 'string' && host.edgeFinish.trim() ? host.edgeFinish : undefined) ?? edgeDefault;
            const profile = finish ? EDGE_FINISH_PROFILE[finish] : undefined;
            if (!profile)
                continue;
            const perimeter = polygonPerimeter(poly);
            const length = r3(Math.max(0, perimeter - Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y)) * scale / 1000);
            if (length > 0)
                result.push({ id: e.id ?? `robe:${fi}:${String(host.id ?? '')}:${ei}`, kind: 'trims', profile, floor: name, room: `${host.name ?? ''} · robe`, area_id: String(host.id ?? ''), area: host.name ?? '', length_m: length, source: 'robe_edge' });
        }
    }
    // Identity belongs to the captured run/segment, never to a profile-wide default. Geometry-only wall,
    // fixture and robe runs stay unresolved until a person chooses their specification.
    const identities = new Map();
    for (const floor of document?.floors ?? [])
        for (const trim of [...(floor.areas ?? []).flatMap(a => a.trims ?? []), ...(floor.trimRuns ?? [])]) {
            if (trim.id)
                identities.set(trim.id, trim);
            for (const segment of trim.segments ?? [])
                identities.set(segment.id, trim);
        }
    return result.map(run => {
        const trim = identities.get(run.id);
        return trim ? { ...run, productID: trim.productID ?? null, variantID: trim.variantID ?? null, specificationStatus: trim.specificationStatus ?? 'unresolved' } : run;
    });
}
/** A fixture's outline in plan points: its drawn boundary, else its rotated L × W around `at`. */
function cutoutOutline(c, scale) {
    const drawn = (c.boundary ?? []).filter((p) => !!p && Number.isFinite(p.x) && Number.isFinite(p.y));
    if (drawn.length >= 3)
        return drawn;
    const at = c.at, L = Number(c.length), W = Number(c.width);
    if (!at || !(L > 0) || !(W > 0) || !(scale > 0))
        return [];
    const hw = L / scale / 2, hh = W / scale / 2, r = (Number(c.rotationDeg) || 0) * Math.PI / 180, cs = Math.cos(r), sn = Math.sin(r);
    return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([dx, dy]) => ({ x: at.x + dx * cs - dy * sn, y: at.y + dx * sn + dy * cs }));
}
/** Supplier quantity conversion is shared by preview and command. Null requests an explicit order quantity. */
/** `overage_pct` is the floor overage (purchase_order_defaults.floor_overage_pct, Jacky's 9 Oct 2026 rule):
 *  the same-unit quantity is ordered that much larger before rounding to whole packs. The CRM calls this
 *  again when a supplier item is changed, so the overage lives on the requirement, not in one caller. */
export function accessoryOrderQuantity(requirement, variant) {
    // Area + wastage + spare (Jacky, 15 Oct 2026): the percentage first, then the spare in the requirement's own unit.
    const need = requirement.quantity * (1 + (requirement.overage_pct ?? 0) / 100) + (requirement.extra ?? 0);
    if (requirement.unit.toLowerCase() === variant.unit.toLowerCase())
        return variant.pack_size ? Math.round(Math.ceil((need - 1e-8) / variant.pack_size) * variant.pack_size * 1000) / 1000 : Math.round(need * 1000) / 1000;
    if (requirement.unit === 'LM' && /^(pc|pcs|piece|pieces)$/i.test(variant.unit) && variant.length_m) {
        const plan = planTrimPurchase((requirement.runs.length ? requirement.runs : [{ id: 'total', label: 'Total', length_m: requirement.quantity }]).map(r => ({ id: r.id, label: r.label, sku: variant.variant_id, lengthMM: r.length_m * 1000, stockLengthMM: variant.length_m * 1000 })));
        return plan.readyToOrder ? plan.piecesToBuy : null;
    }
    return null;
}
//# sourceMappingURL=assessment-edge-scope.js.map